import * as React from 'react';
import styled from 'styled-components';
import gettext from '@sdk/lang/gettext';
import FlexCenteredStyle from '@sdk/components/ui/FlexCenteredStyle';
import View from '@sdk/components/primitives/View';
import BrandLoader from '@sdk/components/ui/BrandLoader';
import { LaborCostCalculatorType } from '@sdk/common/types';
import { ClientFlags, SaveClientFlags } from '@sdk/authentication/types';
import type { Sections } from '@sdk/components/ui/anchorMenu/createSections';
import type { LaborCostSettings, UpdateLaborCostSettings } from '@sdk/labor/types';
import type { AuthUserType } from '@sdk/authentication/types/AuthUserType';

import useLaborCostTabVisited from '@web/settings/hooks/useLaborCostTabVisited';
import SettingsHeader from '@web/settings/components/SettingsHeader';
import GeneralSettings from '@web/settings/components/laborCost/GeneralSettings';
import LaborCostAccess from '@web/settings/components/laborCost/LaborCostAccess';
import Overtime from '@web/settings/components/laborCost/Overtime';
import HolidayPay from '@web/settings/components/laborCost/HolidayPay';
import SpreadOfHours from '@web/settings/components/laborCost/SpreadOfHours';
import LaborPercent from '@web/settings/components/laborCost/LaborPercent';

const LoaderContainer = styled(View)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  ${FlexCenteredStyle}
`;

type Props = {
  laborSettings: LaborCostSettings;
  onLaborCostSettingsChange: UpdateLaborCostSettings;
  sectionsConfig: Sections;
  users: AuthUserType[];
  clientFlags: ClientFlags;
  saveClientFlags: SaveClientFlags;
};

const LaborCostScreen: React.FC<React.PropsWithChildren<Props>> = ({
  laborSettings,
  onLaborCostSettingsChange,
  sectionsConfig,
  users,
  clientFlags,
  saveClientFlags,
}) => {
  useLaborCostTabVisited({ clientFlags, saveClientFlags });
  const isUsCalculatorType = laborSettings?.laborCostCalculatorType === LaborCostCalculatorType.us;

  if (!laborSettings) {
    return (
      <LoaderContainer>
        <BrandLoader />
      </LoaderContainer>
    );
  }

  return (
    <>
      <SettingsHeader
        ref={sectionsConfig.refs.generalSettings}
        withSeparator
        title={gettext('General settings')}
      />
      <GeneralSettings
        laborSettings={laborSettings}
        currencyCode={laborSettings.currency}
        payrollPeriodType={laborSettings.payrollPeriodType}
        payrollPeriodStartDate={laborSettings.payrollPeriodStartDate}
        payrollPeriodStartDateTwo={laborSettings.payrollPeriodStartDateTwo}
        onLaborCostSettingsChange={onLaborCostSettingsChange}
      />

      {isUsCalculatorType && (
        <>
          <SettingsHeader
            ref={sectionsConfig.refs.overtime}
            withSeparator
            title={gettext('Overtime')}
          />
          <Overtime
            overtimeEnabled={laborSettings.overtimeEnabled}
            overtimeWeekThreshold={laborSettings.overtimeWeekThreshold}
            overtimeWeekMultiplier={laborSettings.overtimeWeekMultiplier}
            overtimeDayEnabled={laborSettings.overtimeDayEnabled}
            overtimeDayThreshold={laborSettings.overtimeDayThreshold}
            overtimeDayMultiplier={laborSettings.overtimeDayMultiplier}
            overtimeDoubleDailyEnabled={laborSettings.overtimeDoubleDailyEnabled}
            onLaborCostSettingsChange={onLaborCostSettingsChange}
          />
        </>
      )}

      {isUsCalculatorType && (
        <>
          <SettingsHeader ref={sectionsConfig.refs.holidayPay} title={gettext('Holiday pay')} />
          <HolidayPay
            onLaborCostSettingsChange={onLaborCostSettingsChange}
            holidaysEnabled={laborSettings.holidaysEnabled}
            holidayRateMultiplier={laborSettings.holidayRateMultiplier}
          />
        </>
      )}

      {isUsCalculatorType && (
        <>
          <SettingsHeader
            ref={sectionsConfig.refs.spreadOfHours}
            title={gettext('Spread of hours')}
          />
          <SpreadOfHours
            onLaborCostSettingsChange={onLaborCostSettingsChange}
            spreadOfHoursPay={laborSettings.spreadOfHoursPay}
            spreadOfHoursThreshold={laborSettings.spreadOfHoursThreshold}
            spreadOfHoursEnabled={laborSettings.spreadOfHoursEnabled}
          />
        </>
      )}

      <SettingsHeader ref={sectionsConfig.refs.labor} title={gettext('Labor %')} />
      <LaborPercent
        onLaborCostSettingsChange={onLaborCostSettingsChange}
        laborPercentEnabled={laborSettings.laborPercentEnabled}
        laborPercent={laborSettings.laborPercent}
      />

      <SettingsHeader
        ref={sectionsConfig.refs.laborCostAccess}
        withSeparator
        title={gettext('Labor cost access')}
      />
      <LaborCostAccess
        access={laborSettings.access}
        users={users}
        onLaborCostSettingsChange={onLaborCostSettingsChange}
      />
    </>
  );
};

export default LaborCostScreen;
